import { filter } from 'lodash';
import { reactive, watch } from 'vue';
import { RouteLocationNormalized } from 'vue-router';
import { localStorageModule } from '@/store/application/local.storage';
import { PagePath, PageState, PageTypes } from '@/store/application/page.definitions';
import { getPageStateByPath } from '@/store/application/page.state';
import { tabLabelModule } from '@/store/application/tab.label';
import { languageModule } from '@/store/languages';
import { getItems, getItemsInTabs } from '@/store/menu';
import { MenuItem } from '@/store/menu/menu';
import { settingsItems } from '@/store/menu/settings.items';
import { getRandomId, RouterModule } from '@/store/router';
import { WorkspaceTab } from '@/components/launcher/types';
import { launcherItems } from '../menu/launcher.items';
import { websocketModule } from '@/store/ws/websocket.module';
import { configModule } from '../config';

function fillEmptyIcon(items: MenuItem[], defaultIcon = 'vp-settings') {
  return items.map((v) => ({ ...v, icon: v.icon ?? defaultIcon }));
}

export class WorkspaceModule {
  static Name = 'WorkspaceModule';
  static StoredItems = ['items'];

  redirectLocation: RouteLocationNormalized | null = null;
  items: PageState[] = [];
  current?: string;

  constructor() {
    this.swapTabs = this.swapTabs.bind(this);
  }

  supportPageState(value: string): boolean {
    return value !== 'launcher';
  }

  changeRouteHandler(route: RouteLocationNormalized) {
    const { query } = route;
    const tab: string | undefined = query?.tab ? String(query.tab) : undefined;
    if (tab && this.supportPageState(tab) && !this.hasItemByTab(tab)) {
      const options: any = RouterModule.queryToPageState(query);
      this.addItem(route.path as PagePath, options);
    }
    this.current = tab;
  }

  getTabItemByTab(value: string): WorkspaceTab | undefined {
    return this.computedTabItems.find((tab) => tab.tab === value);
  }

  getItemByTab(value: string): PageState | undefined {
    return this.filteredItems.find((tab) => tab.tab === value);
  }

  get filteredItems() {
    return this.items.filter((v) => !!v);
  }

  get currentItem(): PageState | undefined {
    return this.current && this.supportPageState(this.current) ? this.getItemByTab(this.current) : undefined;
  }

  getTabLabelAndIcon(pageState: PageState) {
    const { pagePath } = pageState;
    const menuItem = this.translatedItems.find((item) => item.path === pagePath);
    return {
      label: tabLabelModule.get(pageState) || menuItem?.title || 'Empty title',
      icon: menuItem?.icon || 'empty'
    };
  }

  get computedTabItems(): WorkspaceTab[] {
    return this.filteredItems.map((v: PageState) => {
      const { tab, id, pagePath: path } = v;
      const { label, icon } = this.getTabLabelAndIcon(v);
      const query = filter({ tab, id, pageState: v ? JSON.stringify(v) : null }, (v: any) => !!v);
      return { label, icon, tab, path, query };
    });
  }

  hasItemByTab(value: string): boolean {
    return this.filteredItems.filter((v) => !!v).findIndex((v) => v.tab === value) > -1;
  }

  removeItemsWithoutViewRights() {
    this.items = this.items.filter((v) => this.hasItemByPagePath(v.pagePath));
  }

  hasItemByPagePath(value: PagePath): boolean {
    return !!getItemsInTabs(false, true).find((item) => item.path === value);
  }

  get translatedItems() {
    return [...launcherItems, ...fillEmptyIcon(settingsItems)].map((item) => {
      return {
        ...item,
        title: languageModule.getTranslatedToken(item.i18n, 'f'),
        description: item.i18n_description && languageModule.getTranslatedToken(item.i18n_description, 'f'),
        locale: languageModule.locale
      };
    });
  }

  addItem(path: PagePath, options?: Partial<PageState>): void {
    const tab = options?.tab || getRandomId();
    const item: PageState = getPageStateByPath(path);
    item.tab = tab;
    if (options) Object.assign(item, options);
    this.items.push(item);
    this.current = tab;
  }

  removeItem(i: number) {
    const index = this.filteredItems.findIndex((item: PageState) => item.tab === this.current);
    if (i === index) {
      let after, before;
      after = this.items[i + 1];
      before = this.items[i - 1];
      this.current = after?.tab || before?.tab || 'launcher';
    }
    this.items.splice(i, 1);
  }

  swapTabs(payload: number[]): void {
    const [firstIndex, secondIndex] = payload;
    const firstItem = this.items[firstIndex];
    const secondItem = this.items[secondIndex];
    this.items[firstIndex] = secondItem;
    this.items[secondIndex] = firstItem;
  }

  syncWsSubscriptions() {
    const subscriptions = websocketModule.subscriptions;
    const hasSubscriptions = Object.values(subscriptions).some((v) => v);
    const pageType = this.currentItem?.pagePath === '/case' ? PageTypes.Case : this.currentItem?.pageType; // FFSEC-5509

    websocketModule.resetSubscriptions();
    if (configModule.features.cases_enabled) { // FFSEC-5509, test feature - try to listen all events for cases
      subscriptions.ws_cases = true;
      subscriptions.ws_case_clusters = true;
    }
    
    switch (pageType) {
      case PageTypes.Cases:
        subscriptions.ws_cases = true;
        websocketModule.updateSubscriptions();
        break;

      case PageTypes.Case:
        subscriptions.ws_cases = true;
        subscriptions.ws_case_clusters = true;
        websocketModule.updateSubscriptions();
        break;
      case PageTypes.Events:
      case PageTypes.Episodes:
      case PageTypes.VideoWall:
        subscriptions.ws_events = true;
        subscriptions.ws_episodes = true;
        websocketModule.updateSubscriptions();
        break;
      case PageTypes.Clusters:
        subscriptions.ws_clusters = true;
        websocketModule.updateSubscriptions();
        break;
      default:
        if (hasSubscriptions) websocketModule.updateSubscriptions();
        break;
    }
  }

  reset() {
    this.items = [];
    this.current = '';
  }

  clearRedirect() {
    this.redirectLocation = null;
  }
}

export const workspaceModule = reactive(new WorkspaceModule());

export function initWorkspaceModule() {
  localStorageModule.registerInstance({ instance: workspaceModule, name: WorkspaceModule.Name, tokens: WorkspaceModule.StoredItems });
  localStorageModule.syncFromStorageByName(WorkspaceModule.Name);
  watch(() => workspaceModule.items, syncWorkspaceModuleToStorage, { deep: true });
  watch(() => workspaceModule.currentItem, workspaceModule.syncWsSubscriptions.bind(workspaceModule));
}

export function syncWorkspaceModuleToStorage() {
  localStorageModule.syncToStorageByName(WorkspaceModule.Name);
}
